import classNames from 'classnames';
import React, { KeyboardEvent, MouseEvent } from 'react';

import Icon from '@wetransfer/transfer/component/icon';
import { useTransferCustomization } from '@wetransfer/transfer/component/TransferCustomizationProvider/hooks';
import { WOODSMOKE } from '@wetransfer/transfer/constant/colors';
import Analytics from '@wetransfer/transfer/modules/analytics';
import I18n from '@wetransfer/transfer/modules/i18n';
import { Pill } from '@wetransfer/transfer/oneui/react';

import styles from './CustomWallpaperOption.module.scss';
import type { CustomWallpaperOptionProps } from './types';

export const CustomWallpaperOption: React.FC<CustomWallpaperOptionProps> = ({
  isUnpaidUser,
  onClickWhileLocked,
}) => {
  const { isEnabledForSender, isDrawerOpen, openDrawer, closeDrawer } =
    useTransferCustomization();

  const trackAppearanceSettingsClicked = () => {
    Analytics.trackAmplitude({
      action: 'Appearance Settings Clicked',
    });
  };

  const handleClick = (event: MouseEvent | KeyboardEvent) => {
    // The CustomWallpaperPopover interferes with this handler so we need to delay by a cycle until we find a better solution.
    setTimeout(() => {
      if (isUnpaidUser) {
        trackAppearanceSettingsClicked();
        onClickWhileLocked(event);
      } else if (isDrawerOpen) {
        closeDrawer();
      } else {
        trackAppearanceSettingsClicked();
        openDrawer();
      }
    }, 0);
  };

  const labelId = `${Math.random()}`;

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'Space':
      case ' ':
      case 'Enter':
        handleClick(event);
        break;
      case 'ArrowRight':
        if (!isDrawerOpen) {
          openDrawer();
        }
        break;
      case 'ArrowLeft':
        if (isDrawerOpen) {
          closeDrawer();
        }
        break;
    }
  };

  return isEnabledForSender ? (
    <div
      role="menuitem"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      id={labelId}
      onClick={handleClick}
      className={classNames(
        'transfer__option',
        'transfer__option-plus',
        isUnpaidUser && 'transfer__option--disabled'
      )}
    >
      <label htmlFor={labelId}>Appearance</label>
      <span className="transfer__option-placeholder">
        Set custom wallpaper
        <Icon
          color={WOODSMOKE}
          type="Arrow"
          orientation="right"
          thickness={2}
        />
      </span>
      {isUnpaidUser ? (
        <Icon type="UpgradeLock" />
      ) : (
        <Pill
          textContent={I18n.t('navigation.label.beta')}
          variant="highlight"
          shape="round"
          size="small"
          className={styles.pill}
        />
      )}
    </div>
  ) : null;
};

CustomWallpaperOption.displayName = 'CustomWallpaperOption';
